import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPage.css";
import "./PlaylistPage.css";
import apiClient from "../api/client";
import Header from "../components/Header";
import usePlaylists from "../hooks/usePlaylists";
import ExportDialog from "./ExportDialog";

function AdminPage({ onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // 右鍵選單狀態
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    user: null,
  });
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // 從 Flask API 取得所有使用者資料
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await apiClient.get("/api/manager/users");
        setUsers(response.data);
      } catch (error) {
        console.error("取得使用者資料發生錯誤:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // 取得歌單群組列表（匯出時用來依組別勾選使用者）
  const { playlists } = usePlaylists();
  const playlistGroups = playlists.map((p) => ({ id: p.id, name: p.name }));

  // 點擊其他地方時關閉右鍵選單
  useEffect(() => {
    const onGlobalClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setContextMenu((prev) => ({ ...prev, visible: false }));
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setContextMenu((prev) => ({ ...prev, visible: false }));
    };
    window.addEventListener("click", onGlobalClick);
    window.addEventListener("contextmenu", onGlobalClick); // 右鍵別保留舊選單
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("click", onGlobalClick);
      window.removeEventListener("contextmenu", onGlobalClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, []);

  const handleConfirmLogout = () => {
    setShowTimeoutModal(false);
    onLogout();
    navigate("/");
  };

  // 右鍵打開自訂選單
  const handleRowContextMenu = (e, user) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      user,
    });
  };

  // 觸發刪除
  const handleDeleteUser = async () => {
    if (!contextMenu.user) return;
    const user = contextMenu.user;

    const ok = window.confirm(`確定要刪除使用者「${user.username}」嗎？此動作無法復原。`);
    if (!ok) return;

    try {
      // 用 id 刪除（也可換 username：/api/manager/users/${user.username}）
      await apiClient.delete(`/api/manager/users/${user.id}`);
      // 前端同步移除
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (error) {
      console.error("刪除失敗：", error);
      alert("刪除失敗，請查看主機日誌或網路連線。");
    } finally {
      setContextMenu({ visible: false, x: 0, y: 0, user: null });
    }
  };

  return (
    <div>
      <Header onLogout={handleConfirmLogout} />
      <div className="admin-container">
        <div className="admin-content">
          <div className="admin-header-row">
            <p className="admin-title">管理者頁面</p>
            <button
              className="add-button export-button"
              onClick={() => setShowExportDialog(true)}
              style={{ backgroundColor: "var(--wa-moss)", color: "white" }}
            >
              📊 匯出聆聽數據 Excel
            </button>
          </div>
          {loading ? (
            <p className="loading-text">載入中...</p>
          ) : (
            <div className="table-scroll">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>帳號</th>
                    <th>密碼</th>
                    <th>綁定UUID</th>
                    <th>開始日期</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((user) => (
                      <tr
                        key={user.username}
                        onClick={() => navigate(`/calendar/${user.username}`)}
                        onContextMenu={(e) => handleRowContextMenu(e, user)}
                        style={{ cursor: "pointer" }}
                        title="左鍵進入行事曆；右鍵開啟操作選單"
                      >
                        <td>{user.id}</td>
                        <td>{user.username}</td>
                        <td>{user.password}</td>
                        <td>{user.bound}</td>
                        <td>{user.startDate}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      {/* 你的表頭有 5 欄，這裡建議 colSpan=5 才不會跑版 */}
                      <td colSpan="5" className="no-data">沒有使用者資料</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 自訂右鍵選單 */}
        {contextMenu.visible && (
          <div
            ref={menuRef}
            className="context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button className="context-menu-item danger" onClick={handleDeleteUser}>
              刪除這位使用者
            </button>
          </div>
        )}

        {/* Modal 彈窗（原有） */}
        {showTimeoutModal && (
          <div className="modal-overlay">
            <div className="modal-box">
              <h3>閒置時間過久</h3>
              <p>您已閒置超過 10 分鐘，已被強制登出!</p>
              <div className="modal-actions">
                <button onClick={handleConfirmLogout}>確定</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <ExportDialog
        visible={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        playlistGroups={playlistGroups}
      />
    </div>
  );
}

export default AdminPage;
